import dayjs from 'dayjs'
import { MerchantSettings } from '@slerp/controls'
import { MessageType } from 'antd/lib/message'
import { MerchantCategory } from '@slerp/controls'

interface UpdateMerchantArrangementSettingsParams {
  variables: {
    merchant: string
    arrangement: MerchantSettings
  }
}

interface CreateCategoryParams {
  variables: {
    merchantSlug: string
    name: string
    description: string
  }
}

interface ToggleArchiveStateParams {
  variables: {
    categoryId: string
    archivedAt: string | null
  }
}

interface ToggleProductArchiveStateParams {
  variables: {
    productIds: Array<string>
    archivedAt: string | null
  }
}

type CreateCategory = {
  data: { createCategory: MerchantCategory }
}

const createCategory = (
  merchantSlug: string,
  name: string,
  description: string,
  merchantSettings: MerchantSettings,
  updateMerchantArrangementSettings: (
    variables: UpdateMerchantArrangementSettingsParams
  ) => Promise<unknown>,
  createCategory: (variables: CreateCategoryParams) => Promise<unknown>,
  errorHandler: (err: Error) => MessageType
): Promise<CreateCategory> => {
  return createCategory({
    variables: {
      merchantSlug,
      name,
      description
    }
    // @ts-ignore
  }).then((result: CreateCategory) => {
    const { category_arrangement } = merchantSettings
    const updatedCategoryArrangement = Object.values(category_arrangement)
    updatedCategoryArrangement.push(result?.data?.createCategory.id)

    updateMerchantArrangementSettings({
      variables: {
        merchant: merchantSlug,
        arrangement: {
          ...merchantSettings,
          category_arrangement: { ...updatedCategoryArrangement }
        }
      }
    }).catch((err: Error) => errorHandler(err))

    return result as CreateCategory
  })
}

interface ToggleCategoryArchiveStateProps {
  categoryId: string
  shouldArchive: boolean
  merchantSlug: string
  merchantSettings: MerchantSettings | null
  toggleArchiveState: (variables: ToggleArchiveStateParams) => Promise<unknown>
  toggleProductArchiveState: (
    variables: ToggleProductArchiveStateParams
  ) => Promise<unknown>
  updateMerchantArrangementSettings: (
    variables: UpdateMerchantArrangementSettingsParams
  ) => Promise<unknown>
  affectedProducts: {
    id: string
    default_variant_id: string
  }[]
}

const toggleCategoryArchiveState = ({
  categoryId,
  shouldArchive,
  merchantSlug,
  merchantSettings,
  toggleArchiveState,
  toggleProductArchiveState,
  updateMerchantArrangementSettings,
  affectedProducts
}: ToggleCategoryArchiveStateProps) => {
  if (!merchantSettings) {
    return Promise.reject(new Error('Merchant settings not found'))
  }

  const hasOne = affectedProducts.length > 0
  const archivedAt = shouldArchive ? dayjs().format() : null
  const updatedCategoryArchiveState = toggleArchiveState({
    variables: {
      categoryId,
      archivedAt
    }
  })

  const productIds = affectedProducts.map((ap) => ap.id)

  const { product_arrangement, category_arrangement } = merchantSettings
  const productArrangement = Object.values(product_arrangement)
  const categoryArrangement = Object.values(category_arrangement)

  const updatedProductArrangement = shouldArchive
    ? productArrangement.filter((id: string) => !productIds.includes(id))
    : [...productArrangement, ...productIds]

  const updatedCategoryArrangement = shouldArchive
    ? categoryArrangement.filter((id: string) => id !== categoryId)
    : [...categoryArrangement, categoryId]

  const archiveOrRestoreProducts = hasOne
    ? toggleProductArchiveState({
        variables: {
          productIds: productIds,
          archivedAt: shouldArchive ? dayjs().format() : null
        }
      })
    : () => Promise.resolve()

  const updatedArrangement = updateMerchantArrangementSettings({
    variables: {
      merchant: merchantSlug,
      arrangement: {
        ...merchantSettings,
        category_arrangement: { ...updatedCategoryArrangement },
        ...(hasOne && { product_arrangement: { ...updatedProductArrangement } })
      }
    }
  })

  return Promise.all([
    updatedCategoryArchiveState,
    archiveOrRestoreProducts,
    updatedArrangement
  ])
}

export { toggleCategoryArchiveState, createCategory }
